/* eslint-disable */
// ** React Imports
import { forwardRef } from 'react';

// ** Third Party Components
import classnames from 'classnames';

// ** Reactstrap Imports
import { ReactstrapBadge as Badge } from '@febos-io/design';

interface AvatarProps {
  /** URL of an image to display. */
  img?: string;

  /** React node to render as an icon (e.g., using `MaterialUI` icons). */
  icon?: React.ReactNode;

  /** Alternative URL for an image (deprecated, use `img` instead). */
  src?: string;

  /** Whether to display a badge in the upper right corner. */
  badgeUp?: boolean;

  /** Text content to display within the Avatar itself. */
  content?: string;

  /** Text content for the badge (if `badgeUp` is true). */
  badgeText?: string;

  /** Additional CSS class names for the main Avatar element. */
  className?: string;

  /** Additional CSS class names for the image element (if `img` is used). */
  imgClassName?: string;

  /** Inline styles for the content element. */
  contentStyles?: React.CSSProperties;

  /** Size of the Avatar (default: 'md'). */
  size?: 'sm' | 'md' | 'lg' | 'xl';

  /** HTML tag name or a function that returns a React element for the Avatar. */
  tag?: string | React.FC;

  /** Online status indicator (for custom use cases). */
  status?: 'online' | 'offline' | 'away' | 'busy';

  /** Height of the image (if `img` is used). */
  imgHeight?: string | number;

  /** Width of the image (if `img` is used). */
  imgWidth?: string | number;

  /** Color for the badge (if `badgeUp` is true). */
  badgeColor?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'danger'
    | 'info'
    | 'warning'
    | 'dark'
    | 'light-primary'
    | 'light-secondary'
    | 'light-success'
    | 'light-danger'
    | 'light-info'
    | 'light-warning'
    | 'light-dark';

  /** Color for the Avatar itself. */
  color?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'danger'
    | 'info'
    | 'warning'
    | 'dark'
    | 'light-primary'
    | 'light-secondary'
    | 'light-success'
    | 'light-danger'
    | 'light-info'
    | 'light-warning'
    | 'light-dark';

  /** Initials to display if no image or icon is provided. */
  initials?: string | React.ReactNode;
}

const Avatar = forwardRef((props: AvatarProps, ref) => {
  // ** Props
  const {
    img,
    size,
    icon,
    color,
    status,
    badgeUp,
    content,
    tag: Tag = 'div',
    initials,
    imgWidth,
    className,
    badgeText,
    imgHeight,
    badgeColor,
    imgClassName,
    contentStyles,
    ...rest
  } = props;

  // ** Function to extract initials from content
  const getInitials = (name?: string) => {
    if (!name){
      return null;
    }
    const results: string[] = [];
    const wordArray = name.split(' ');
    wordArray.forEach((e) => {
      results.push(e[0]);
    });
    return results.join('');
  };

  return (
    <Tag
      // @ts-ignore
      className={classnames('avatar', {
        [`bg-${color}`]: color,
        [`avatar-${size}`]: size,
        // check if has className and add to object
        ...(
          className && { [className]: className }
        )
      })}
      ref={ref}
      {...rest}
    >
      {!img || img === undefined ? (
        <span
          className={classnames('avatar-content', {
            'position-relative': badgeUp
          })}
          style={contentStyles}
        >
          {initials ? getInitials(content) : content}

          {icon || null}
          {badgeUp ? (
            <Badge color={badgeColor || 'primary'} className="badge-sm badge-up" pill>
              {badgeText || '0'}
            </Badge>
          ) : null}
        </span>
      ) : (
        <img
          className={classnames({
            ...(imgClassName && {[imgClassName]: imgClassName})
          })}
          src={img}
          alt="avatarImg"
          height={imgHeight && !size ? imgHeight : 32}
          width={imgWidth && !size ? imgWidth : 32}
        />
      )}
      {status ? (
        <span
          className={classnames({
            [`avatar-status-${status}`]: status,
            [`avatar-status-${size}`]: size
          })}
        />
      ) : null}
    </Tag>
  );
});

export default Avatar;
